<template>
  <div id="collection-view">
    <h1 class="collection-title text-start pt-10">
      <span class="accent--text">{{ $t('shared.unique') }}. </span>
      <span class="primary--text">{{ $t('shared.rare') }}. </span>
      <span class="secondary--text">{{ $t('shared.handmade') }}.</span>
    </h1>
    <h2 class="pt-2 pb-5 text-start collection-subtitle">{{ $t('shared.whatYouStand') }}</h2>
    <div class="pa-12 text-center" v-if="isLoading">
      <v-progress-circular indeterminate class="mx-auto" size="60" width="5" />
    </div>
    <template v-else>
      <v-row>
        <v-col v-for="(optionsInTrait, traitName) in traitsAndOptions"
          :key="'filter-' + traitName"
          cols="24" sm="12" md="8" lg="6"
          class="px-2"
        >
          <v-select-ext
            :label="traitName"
            v-model="traitFilters[traitName]"
            @change="traitFilters = { ...traitFilters }"
            :items="Object.keys(optionsInTrait)"
            multiple
            color="primary"
          >
            <template #selection="{ index: selectionIndex, parent: selectorParent }">
              <span v-if="!selectionIndex" class="w-100 text-truncate">{{ selectorParent.selectedItems.join(', ') }}</span>
            </template>
          </v-select-ext>
        </v-col>
      </v-row>
      <v-data-iterator
        :items="filteredTokensData"
        :items-per-page.sync="tableItemsPerPage"
        :footer-props="{
          itemsPerPageOptions: [ 12, 24, 48, 96, totalTokens]
        }"
      >
        <template v-slot:default="{ items: tokensInPage }">
          <v-row justify="center" align-content="stretch">
            <v-col v-for="tokenData of tokensInPage"
              :key="'token-col-' + tokenData.tokenId"
              cols="24" sm="12" md="8" lg="6"
              class="pa-2 text-center"
            >
              <qb-token-item-card :token-data="tokenData" />
            </v-col>
          </v-row>
        </template>
      </v-data-iterator>
    </template>
  </div>
</template>

<style lang="scss">
  @import '@/styles/main.scss';

  #collection-view {
    margin-bottom: 300px;
    h1.collection-title {
      font-size: 42px;
      line-height: 42px;
      @include mdAndUp {
        font-size: 70px;
        line-height: 70px;
      }
    }
    h2.collection-subtitle {
      font-size: 20px;
      line-height: 20px;
      @include mdAndUp {
        font-size: 42px;
        line-height: 42px;
      }
    }
  }

</style>

<script>
import { totalTokens, defaultMetadataUri } from '@/data/constants.js'
import QbTokenItemCard from '@/components/nfts/QbTokenItemCard.vue'
import { translateIpfsUri } from '@/filters'

export default {
  components: {
    QbTokenItemCard
  },
  data () {
    return {
      isLoading: true,
      metadataUri: null,
      tokensData: [],
      traitFilters: {},
      tableItemsPerPage: 12
    }
  },
  metaInfo () {
    return {
      title: 'The QBits - ' + this.$t('nav.collection')
    }
  },
  computed: {
    totalTokens: () => totalTokens,
    web3LastChangeTimestamp () {
      return this.$store.getters.web3LastChangeTimestamp
    },
    ethProvider () {
      return this.$store.getters.ethProvider
    },
    ethContractInstance () {
      return this.$store.getters.ethContractInstance
    },
    isWeb3Connected () {
      return !!this.ethProvider
    },
    isChainMatched () {
      return this.$store.getters.web3ChainMatch
    },
    isWeb3ConnectedToChain () {
      return this.isWeb3Connected && this.isChainMatched
    },
    traitsAndOptions () {
      if (!this.tokensData) { return {} }
      const traitsAndOptions = {}
      this.tokensData.forEach(tokenData => {
        if (!tokenData.attributes) { return }
        tokenData.attributes.forEach(({
          trait_type: tokenTrait,
          value: traitValue }) => {
          if (!traitsAndOptions[tokenTrait]) {
            traitsAndOptions[tokenTrait] = {}
          }
          traitsAndOptions[tokenTrait][traitValue] = (traitsAndOptions[tokenTrait][traitValue] || 0) + 1
        })
      })
      return traitsAndOptions
    },
    filteredTokensData () {
      if (!this.tokensData) { return [] }
      if (!this.tokensData) { return [] }
      return this.tokensData.filter(tokenData => (
        Object.entries(this.traitFilters)
          .every(([filterTrait, filterValues]) => (
            !filterValues || !filterValues.length || filterValues
              .includes((tokenData.attributes
                .find(tokenAttr => tokenAttr.trait_type === filterTrait) || {}).value
              )
          ))
      ))
    }
  },
  methods: {
    updateInfoFromChain () {
      if (false && this.isWeb3ConnectedToChain) {
        this.ethContractInstance.baseURI()
          .then(result => { this.metadataUri = result + 'all' })
          .catch(() => {
            this.metadataUri = defaultMetadataUri
            this.isLoading = false
          })
      } else {
        this.metadataUri = defaultMetadataUri
        this.isLoading = false
      }
    },
    async updateCollectionData () {
      if (!this.metadataUri) {
        this.tokensData = []
        return
      }
      this.isLoading = true
      let tokensDataArray = []
      try {
        const tokenMetadataResponse = await fetch(this.metadataUri, {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        })
        if (!tokenMetadataResponse.ok) { throw new Error() }
        const tokenMetadata = await tokenMetadataResponse.json()
        tokensDataArray = Object.entries(tokenMetadata)
          .map(([tokenIdStr, tokenMetadata]) => ({
            tokenId: Number(tokenIdStr),
            title: tokenMetadata.name,
            imageUri: translateIpfsUri(
              tokenMetadata.image
                .replace(/\{id\}}/g, String(tokenIdStr))
            ),
            description: tokenMetadata.description,
            attributes: tokenMetadata.attributes
          }))
      } catch (error) {
        console.error(error) // eslint-disable-line no-console
        this.$store.dispatch('alertShow', { error: this.$t('error.fetchingTokensData') })
      }
      this.tokensData = tokensDataArray.sort((a, b) => (a.tokenId - b.tokenId))
      this.isLoading = false
    }
  },
  watch: {
    web3LastChangeTimestamp () {
      this.updateInfoFromChain()
    },
    metadataUri () {
      this.updateCollectionData()
    }
  },
  mounted () {
    this.updateInfoFromChain()
  }
}
</script>
